import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CloudOff, FileText, Lightbulb, Loader2, Maximize2, Minus, Plus, Sparkles, Users, X } from 'lucide-react';
import {
  addRun, arrangeIdeas, canChangeIdea, giveIdea, namedAnswer, namesFromAnswer, setAsk, setDone, setLocked, setName, setPlace, setScreenplay, setText, titleOf, toggleReaction,
  type AskRun, type Idea, type IdeaAsk, type MadeBy, type WorkspacePerson,
} from '../utils/ideas';
import { addVersion, appendText, fountainFromAnswer, paginate, parseFountain, replaceScene, withTitlePage, type ScreenplayMeta } from '../utils/screenplay';
import { SCREENPLAY_CARD_WIDTH, ScreenplayCard, type ScreenplayAsk } from './ideas/ScreenplayCard';
import {
  changeIdea, createIdea, createIdeas, deleteIdea, finishIdea, giveIdeaTo, keepIdea, reactToIdea, recordAskRun, saveIdeaPlaces, saveIdeaWords, watchIdeas, type IdeaUser,
} from '../utils/ideaStore';
import {
  CARD_TOOLS, cardToolRequest, chooseTextModel, chooseThinking, costOfUsage, generateText, nameRequest, rememberTextModel, rememberThinking, screenplayRequest, textModelAvailable, thinkRequest,
  textModelName, textModelOptions, withThinking, type CardTool, type TextAnswer, type TextModelId, type TextModelKeys, type TextRequest,
} from '../utils/textModel';
import { veniceTextModels, type VeniceTextModel } from '../utils/venice';
import { fixPromptRequest, frameOf, generatorOf, lengthOf, loadPromptSkill, promptCardFromAnswer, promptRequest, workspaceNotes, type PromptFor, type PromptToCanvas } from '../utils/promptWriter';
import type { CanvasNode } from '../types';
import { COLUMN, GAP, UNMEASURED, cardWidth, freeSpotNear, placeBlocks, type LayoutBlock, type Point, type Rect } from '../utils/boardLayout';
import { refreshVeniceBalance } from '../hooks/useVeniceBalance';
import { IdeaCard } from './ideas/IdeaCard';
import { AskCard } from './ideas/AskCard';
import { errorText, useNow } from './ideas/parts';

interface IdeasPageProps {
  /** The open workspace in the cloud; null for one on this device only, or when signed out */
  projectId: string | null;
  user: IdeaUser | null;
  /** The workspace's people: its owner and everyone it is shared with */
  people: WorkspacePerson[];
  isViewOnly?: boolean;
  /** The keys the writing tools may use (Settings) */
  textKeys?: TextModelKeys;
  /** A card a link or the canvas asked for; shown once the cards are in, then forgotten */
  askedIdeaId?: string | null;
  onAskedIdeaOpened?: () => void;
  /** Opens the workspace's Share dialog: the people of the workspace are the people of its cards */
  onShare: () => void;
  /** The library panel is open on the left: the rail moves aside for it, as the node rail does */
  panelOpen?: boolean;
  /** A screenplay is opened full over everything: the app hides its chrome but the account button */
  onFocusChange?: (open: boolean) => void;
  /** The workspace's nodes: what a prompt may name (its references, characters, props and places) */
  nodes?: CanvasNode[];
  /** A prompt card taken to the canvas as a Video or Image node; absent where there is no canvas at hand */
  onSendToCanvas?: (card: PromptToCanvas) => void | Promise<void>;
}

const NO_NODES: CanvasNode[] = [];

/** The board's measures, in its own pixels before zoom */
const PAD = 48;
const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;
/** The app's top bar lies over the board: the first row of cards starts under it */
const TOP_BAR = 72;
/** The height a card of a kind is given before it is measured, to find it a free spot */
const GUESSED_HEIGHT: Record<Idea['kind'], number> = { card: UNMEASURED, ask: 460, screenplay: 560 };

interface View { x: number; y: number; scale: number }
const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/** How wide a card is: a model's card twice a written one, a screenplay a sheet of paper */
const widthOf = (idea: Pick<Idea, 'kind' | 'source'>): number =>
  idea.kind === 'screenplay' ? SCREENPLAY_CARD_WIDTH : idea.kind === 'card' && idea.source === 'ai' ? cardWidth(2) : COLUMN;
/** How many of the board's columns a width takes */
const spanOf = (width: number): number => Math.max(1, Math.round((width + GAP) / (COLUMN + GAP)));

/**
 * The workspace's Ideas page: a board for thinking, moved about like the node canvas (scroll
 * or drag to pan, Ctrl+scroll to zoom), its cards dragged wherever anyone wants them. Cards of
 * writing are kept when liked, reacted to, given out as tasks; a Think up card asks a model
 * for cards; a screenplay card is a sheet of paper written on in place. It lies over the node
 * canvas, under the app's top bar, so the library, the settings and the balance stay at hand.
 */
export const IdeasPage: React.FC<IdeasPageProps> = ({
  projectId, user, people, isViewOnly = false, textKeys = {}, askedIdeaId = null, onAskedIdeaOpened, onShare, panelOpen = false, onFocusChange, nodes = NO_NODES, onSendToCanvas,
}) => {
  const now = useNow();
  const [ideas, setIdeas] = useState<Idea[] | null>(null);
  /** The cloud's cards as last heard, for what is shown ahead of it to be checked against */
  const ideasRef = useRef<Idea[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  const [madeId, setMadeId] = useState<string | null>(null);
  const [litId, setLitId] = useState<string | null>(null);
  /** The screenplay card opened full over the board */
  const [openId, setOpenId] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const [working, setWorking] = useState<{ id: string; tool: CardTool | 'name' | 'ask' | 'fix' } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Changes shown before the cloud answers, by card; dropped once the cloud's copy has caught up
  const [overrides, setOverrides] = useState<Record<string, Idea>>({});
  const canEdit = !!user && !!projectId && !isViewOnly;
  const toolsAvailable = textModelAvailable(textKeys);

  // The model that writes: the one chosen last time on this device, among those the keys can ask.
  // Venice's catalog is read once per session, so its whole list is offered.
  const [catalog, setCatalog] = useState<VeniceTextModel[] | null>(null);
  const [modelId, setModelId] = useState<TextModelId | null>(() => chooseTextModel(textKeys));
  const options = useMemo(() => textModelOptions(textKeys, catalog), [textKeys, catalog]);
  useEffect(() => {
    if (options.length > 0 && !options.some(option => option.id === modelId)) setModelId(chooseTextModel(textKeys, options));
  }, [options, modelId, textKeys]);
  useEffect(() => {
    if (!textKeys.venice || catalog || !user || !projectId) return;
    veniceTextModels().then(setCatalog, (e) => console.warn('[ideas] Venice model list not read:', e));
  }, [textKeys.venice, catalog, user, projectId]);
  const chooseModel = (id: TextModelId) => { setModelId(id); rememberTextModel(id); };
  const modelName = textModelName(textKeys, modelId, options);
  const modelOption = options.find(option => option.id === modelId) ?? null;
  // How hard a thinking model thinks: chosen on the card, remembered on this device; nothing for a model that does not think
  const [thinking, setThinking] = useState<string | null>(() => chooseThinking(modelOption));
  useEffect(() => { setThinking(chooseThinking(modelOption)); }, [modelOption]);
  const chooseThinkingLevel = (effort: string) => { setThinking(effort); rememberThinking(effort); };

  useEffect(() => {
    if (!projectId || !user) { setIdeas(null); return; }
    setIdeas(null);
    setLoadError(null);
    const stop = watchIdeas(projectId, (list) => { ideasRef.current = list; setIdeas(list); }, (message) => { setLoadError(message); ideasRef.current = []; setIdeas([]); });
    return stop;
  }, [projectId, user, round]);

  // The cloud's copy wins once it is as new as what was shown ahead of it (a card the cloud
  // has not sent yet, one just made, stays as shown)
  useEffect(() => {
    if (!ideas) return;
    setOverrides(prev => {
      const kept = Object.fromEntries(Object.entries(prev).filter(([id, local]) => {
        const remote = ideas.find(idea => idea.id === id);
        return !remote || remote.updatedAt < local.updatedAt;
      }));
      return Object.keys(kept).length === Object.keys(prev).length ? prev : kept;
    });
  }, [ideas]);

  const shown = useMemo(() => {
    const known = ideas ?? [];
    const ahead = Object.values(overrides).filter(local => !known.some(idea => idea.id === local.id));
    return arrangeIdeas([...known.map(idea => overrides[idea.id] ?? idea), ...ahead]);
  }, [ideas, overrides]);
  const keptTexts = useMemo(() => shown.filter(idea => idea.locked && idea.kind === 'card').map(idea => idea.text).slice(0, 12), [shown]);
  // An opened screenplay that is gone (deleted, or the workspace changed) closes
  useEffect(() => { if (openId && !shown.some(idea => idea.id === openId)) setOpenId(null); }, [openId, shown]);
  // While one is open the app's chrome steps away; when this page goes, so does that
  useEffect(() => { onFocusChange?.(openId !== null); }, [openId, onFocusChange]);
  useEffect(() => () => onFocusChange?.(false), [onFocusChange]);

  // ── The view: panned and zoomed like the canvas ──
  const viewportRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const view = useRef<View>({ x: PAD, y: TOP_BAR + 16, scale: 1 });
  const [zoomPercent, setZoomPercent] = useState(100);
  const fitted = useRef(false);
  const applyView = useCallback(() => {
    const { x, y, scale } = view.current;
    if (layerRef.current) layerRef.current.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    setZoomPercent(Math.round(scale * 100));
  }, []);
  const setView = useCallback((next: Partial<View>) => { view.current = { ...view.current, ...next }; applyView(); }, [applyView]);

  const zoomAt = useCallback((factor: number, clientX?: number, clientY?: number) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const rect = vp.getBoundingClientRect();
    const cx = clientX === undefined ? rect.width / 2 : clientX - rect.left;
    const cy = clientY === undefined ? rect.height / 2 : clientY - rect.top;
    const { x, y, scale } = view.current;
    const next = clamp(scale * factor, MIN_SCALE, MAX_SCALE);
    setView({ x: cx - (cx - x) * (next / scale), y: cy - (cy - y) * (next / scale), scale: next });
  }, [setView]);

  /** The whole board in view, under the top bar */
  const fit = useCallback(() => {
    const vp = viewportRef.current;
    const layer = layerRef.current;
    if (!vp || !layer) return;
    const width = layer.offsetWidth;
    const height = layer.offsetHeight;
    if (width <= 0 || height <= 0) return;
    const scale = clamp(Math.min((vp.clientWidth - 32) / width, (vp.clientHeight - TOP_BAR - 100) / height, 1), MIN_SCALE, MAX_SCALE);
    setView({ scale, x: Math.max(16, (vp.clientWidth - width * scale) / 2), y: Math.max(TOP_BAR + 16, (vp.clientHeight + TOP_BAR - height * scale) / 2) });
  }, [setView]);

  /** A card brought to the middle of the view, at the zoom as it is, or zoomed out until the whole card fits */
  const panTo = useCallback((ideaId: string, fitCard: boolean = false) => {
    const vp = viewportRef.current;
    const el = layerRef.current?.querySelector<HTMLElement>(`[data-card-slot="${ideaId}"]`);
    if (!vp || !el) return;
    let { scale } = view.current;
    if (fitCard && el.offsetHeight > 0) scale = clamp(Math.min(scale, (vp.clientHeight - TOP_BAR - 48) / el.offsetHeight, (vp.clientWidth - 64) / el.offsetWidth), MIN_SCALE, 1);
    setView({
      scale,
      x: vp.clientWidth / 2 - (el.offsetLeft + el.offsetWidth / 2) * scale,
      y: (vp.clientHeight + TOP_BAR) / 2 - (el.offsetTop + el.offsetHeight / 2) * scale,
    });
  }, [setView]);

  // Scroll pans, Ctrl or Cmd and scroll zooms at the pointer (a pinch arrives the same way); a
  // field or a box that can scroll keeps its own scrolling
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement;
      const scroller = target.closest<HTMLElement>('textarea, select, [data-card-menu], [data-scrolls]');
      if (scroller && scroller.scrollHeight > scroller.clientHeight) {
        const atTop = scroller.scrollTop <= 0 && e.deltaY < 0;
        const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1 && e.deltaY > 0;
        if (!atTop && !atBottom) return;
      }
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        const notch = e.deltaMode !== 0 || Math.abs(e.deltaY) >= 50;
        zoomAt(Math.exp(-e.deltaY * (e.ctrlKey && !e.metaKey && !notch ? 0.01 : 0.002)), e.clientX, e.clientY);
      } else {
        setView({ x: view.current.x - e.deltaX, y: view.current.y - e.deltaY });
      }
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [zoomAt, setView]);

  // A drag on the board itself pans it
  const drag = useRef<{ pointerId: number; startX: number; startY: number; viewX: number; viewY: number; moved: boolean } | null>(null);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-idea-card], [data-board-bar], button, select, textarea, input, a, label')) return;
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, viewX: view.current.x, viewY: view.current.y, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.currentTarget.setAttribute('data-panning', '1');
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < 3) return;
    d.moved = true;
    setView({ x: d.viewX + dx, y: d.viewY + dy });
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== e.pointerId) return;
    drag.current = null;
    e.currentTarget.removeAttribute('data-panning');
  };

  // ── The arrangement: every card where someone put it; the page places the rest ──
  const [heights, setHeights] = useState<Record<string, number>>({});
  const observers = useRef(new Map<string, ResizeObserver>());
  const slotRefs = useRef(new Map<string, (el: HTMLDivElement | null) => void>());
  /** The ref of a card's slot: its height is measured, and measured again whenever it changes */
  const slotRef = (id: string) => {
    let callback = slotRefs.current.get(id);
    if (!callback) {
      callback = (el) => {
        observers.current.get(id)?.disconnect();
        observers.current.delete(id);
        if (!el) return;
        const record = () => setHeights(prev => (prev[id] === el.offsetHeight ? prev : { ...prev, [id]: el.offsetHeight }));
        record();
        if (typeof ResizeObserver !== 'undefined') {
          const observer = new ResizeObserver(record);
          observer.observe(el);
          observers.current.set(id, observer);
        }
      };
      slotRefs.current.set(id, callback);
    }
    return callback;
  };
  useEffect(() => () => { for (const observer of observers.current.values()) observer.disconnect(); }, []);

  /** The board is laid out by hand once anyone has moved a card on it; until then the page arranges it */
  const byHand = shown.some(idea => idea.at);
  /**
   * What is placed as one. On a board laid out by hand every card is its own: where it was put,
   * or where the page finds room. Until then a Think up card and its answers make a row, the
   * oldest answer next to it and each new one further right; any other card is on its own.
   */
  const blocks = useMemo((): LayoutBlock[] => {
    if (byHand) return shown.map(idea => ({ id: idea.id, cards: [{ id: idea.id, width: widthOf(idea) }], at: idea.at }));
    const out: LayoutBlock[] = [];
    for (let i = 0; i < shown.length; i++) {
      const idea = shown[i];
      if (idea.kind !== 'ask') { out.push({ id: idea.id, cards: [{ id: idea.id, width: widthOf(idea) }], at: null }); continue; }
      const answers: Idea[] = [];
      while (i + 1 < shown.length && shown[i + 1].fromAskId === idea.id) answers.push(shown[++i]);
      answers.sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
      out.push({ id: idea.id, cards: [idea, ...answers].map(card => ({ id: card.id, width: widthOf(card) })), at: null });
    }
    return out;
  }, [shown, byHand]);
  // As many columns as the widest row needs; four otherwise, fewer for a few cards
  const units = shown.reduce((sum, idea) => sum + spanOf(widthOf(idea)), 0);
  const columns = Math.max(1, Math.min(4, units), ...blocks.map(block => spanOf(block.cards.reduce((sum, card) => sum + card.width, 0) + (block.cards.length - 1) * GAP)));
  const placement = useMemo(() => placeBlocks(blocks, heights, columns), [blocks, heights, columns]);
  const layout = useMemo(() => ({
    places: Object.fromEntries(shown.map(idea => [idea.id, { ...placement.cards[idea.id], measured: heights[idea.id] !== undefined }])) as Record<string, Rect & { measured: boolean }>,
    // Cards may sit left of or above where the board began: the board's box starts at the leftmost and topmost
    left: placement.left,
    top: placement.top,
    width: placement.width,
    height: placement.height,
    all: shown.every(idea => heights[idea.id] !== undefined),
  }), [shown, placement, heights]);

  // When the board's box grows to the left or upwards (a card went past where the board began), the
  // view follows the box's new origin, so the dragged card is what moves on the screen and nothing else
  const origin = useRef({ left: layout.left, top: layout.top });
  useLayoutEffect(() => {
    const dx = layout.left - origin.current.left;
    const dy = layout.top - origin.current.top;
    origin.current = { left: layout.left, top: layout.top };
    if (dx || dy) setView({ x: view.current.x + dx * view.current.scale, y: view.current.y + dy * view.current.scale });
  }, [layout.left, layout.top, setView]);

  // ── Moving cards: a drag on a card takes it wherever the pointer goes, as a node on the canvas ──
  const [moving, setMoving] = useState<{ id: string; dx: number; dy: number } | null>(null);
  /** A click right after a drag is the drag's end, not a click on the card */
  const justMoved = useRef(false);
  const move = useRef<{ pointerId: number; id: string; startX: number; startY: number; origin: Rect; moved: boolean; dx: number; dy: number; handle: HTMLElement } | null>(null);
  /** Who may move a card: whoever may change it (a Think up card: anyone who may edit the workspace) */
  const mayPlace = (idea: Idea) => canEdit && !!user && (idea.kind === 'ask' || canChangeIdea(idea, user.id, canEdit, people));
  /** What a press on a card does itself, rather than move the card */
  const NOT_A_HANDLE = 'button, textarea, input, select, a, summary, label, [data-card-prompt-text], [data-card-menu], [role="menu"], [data-screenplay-editor]';
  /** Whether a press here moves the card: the head moves it whatever is under the pointer but its tools and a field (a plain click still reaches what it hit); elsewhere anything that is not a control */
  const isHandle = (target: HTMLElement): boolean =>
    (target.closest('[data-card-head]') ? !target.closest('[data-card-tools], input, select, textarea') : !target.closest(NOT_A_HANDLE));

  /** Where cards are now, saved for everyone; shown at once */
  const savePlaces = (places: { lead: Idea; at: Point | null }[]) => {
    if (!projectId || places.length === 0) return;
    const now = Date.now();
    for (const place of places) showAhead(setPlace(place.lead, place.at, now));
    saveIdeaPlaces(projectId, places.map(place => ({ id: place.lead.id, at: place.at }))).catch((err) => {
      for (const place of places) dropAhead(place.lead.id);
      setNotice(errorText(err, 'The cards could not be moved.'));
    });
  };

  const onCardPointerDown = (idea: Idea) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || !mayPlace(idea) || !isHandle(e.target as HTMLElement)) return;
    const origin = placement.cards[idea.id];
    if (!origin) return;
    move.current = { pointerId: e.pointerId, id: idea.id, startX: e.clientX, startY: e.clientY, origin, moved: false, dx: 0, dy: 0, handle: e.currentTarget };
    e.stopPropagation(); // not a pan of the board
  };
  /** Grabbing a card by its words selects none of them */
  const onCardMouseDown = (idea: Idea) => (e: React.MouseEvent) => {
    if (e.button === 0 && mayPlace(idea) && isHandle(e.target as HTMLElement)) e.preventDefault();
  };
  const onCardPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const m = move.current;
    if (!m || m.pointerId !== e.pointerId) return;
    if (!(e.buttons & 1)) { move.current = null; setMoving(null); return; } // released somewhere it was not seen
    if (!m.moved) {
      if (Math.abs(e.clientX - m.startX) + Math.abs(e.clientY - m.startY) < 4) return;
      m.moved = true;
      // From here the card follows the pointer wherever it goes; held only now, so a plain click still reaches what it hit
      m.handle.setPointerCapture?.(e.pointerId);
    }
    const scale = view.current.scale;
    // Wherever the pointer takes it, as a node on the canvas: past where the board began too
    m.dx = (e.clientX - m.startX) / scale;
    m.dy = (e.clientY - m.startY) / scale;
    setMoving({ id: m.id, dx: m.dx, dy: m.dy });
    e.stopPropagation();
  };
  const onCardPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const m = move.current;
    if (!m || m.pointerId !== e.pointerId) return;
    move.current = null;
    setMoving(null);
    if (!m.moved) return;
    e.stopPropagation();
    justMoved.current = true;
    setTimeout(() => { justMoved.current = false; }, 0);
    const idea = shown.find(candidate => candidate.id === m.id);
    if (!idea) return;
    const places: { lead: Idea; at: Point | null }[] = [{ lead: idea, at: { x: m.origin.x + m.dx, y: m.origin.y + m.dy } }];
    // The first move on a board the page arranged: every other card stays where it is now
    if (!byHand) {
      for (const other of shown) {
        if (other.id === m.id || !mayPlace(other) || !placement.cards[other.id]) continue;
        places.push({ lead: other, at: { x: placement.cards[other.id].x, y: placement.cards[other.id].y } });
      }
    }
    savePlaces(places);
  };
  const onCardClickCapture = (e: React.MouseEvent) => { if (justMoved.current) { e.stopPropagation(); e.preventDefault(); } };
  /** A card's place, moved along while it is dragged */
  const placeOf = (ideaId: string) => {
    const place = layout.places[ideaId];
    return moving && moving.id === ideaId ? { ...place, x: place.x + moving.dx, y: place.y + moving.dy } : place;
  };

  /** The middle of what is in view, in the board's own pixels */
  const viewMiddle = (): Point => {
    const vp = viewportRef.current;
    const { x, y, scale } = view.current;
    return { x: ((vp?.clientWidth ?? 0) / 2 - x) / scale - PAD + layout.left, y: (((vp?.clientHeight ?? 0) + TOP_BAR) / 2 - y) / scale - PAD + layout.top };
  };

  // The board is fitted to the view once its cards are in and measured
  useEffect(() => {
    if (!ideas || fitted.current || (ideas.length > 0 && !layout.all)) return;
    fitted.current = true;
    requestAnimationFrame(() => (ideas.length > 0 ? fit() : applyView()));
  }, [ideas, layout.all, fit, applyView]);
  useEffect(() => { fitted.current = false; }, [projectId]);

  // What a link asked for: lit up and brought into view, once the cards are in
  useEffect(() => {
    if (!ideas || !askedIdeaId) return;
    if (shown.some(idea => idea.id === askedIdeaId)) {
      setLitId(askedIdeaId);
      requestAnimationFrame(() => panTo(askedIdeaId));
      const timer = setTimeout(() => setLitId(null), 4000);
      onAskedIdeaOpened?.();
      return () => clearTimeout(timer);
    }
    setNotice('That card is no longer here.');
    onAskedIdeaOpened?.();
  }, [ideas, shown, askedIdeaId, onAskedIdeaOpened, panTo]);

  /** What is shown ahead of the cloud right now, by card, known at once (the state above catches up a render later) */
  const aheadRef = useRef<Record<string, Idea>>({});
  /** Show a card ahead of the cloud, unless the cloud, or what is shown already, is as new or newer (an acknowledgement that comes late must not put an older copy back) */
  const showAhead = useCallback((next: Idea) => {
    const remote = ideasRef.current?.find(idea => idea.id === next.id);
    if (remote && remote.updatedAt >= next.updatedAt) return;
    const ahead = aheadRef.current[next.id];
    if (ahead && ahead.updatedAt > next.updatedAt) return;
    aheadRef.current[next.id] = next;
    setOverrides(prev => (prev[next.id] && prev[next.id].updatedAt > next.updatedAt ? prev : { ...prev, [next.id]: next }));
  }, []);
  const dropAhead = useCallback((id: string) => {
    delete aheadRef.current[id];
    setOverrides(prev => { const { [id]: _gone, ...rest } = prev; return rest; });
  }, []);
  /** A card as it is now: what is shown ahead of the cloud, or the cloud's copy, whichever is newer; a handler's own copy may be older than both */
  const freshest = useCallback((idea: Idea): Idea => {
    const candidates = [aheadRef.current[idea.id], ideasRef.current?.find(known => known.id === idea.id), idea].filter((candidate): candidate is Idea => !!candidate);
    return candidates.reduce((best, candidate) => (candidate.updatedAt > best.updatedAt ? candidate : best));
  }, []);
  const clearMade = useCallback(() => setMadeId(null), []);

  /** A free spot for a new card of this size, nearest to where it is wanted, on a board laid out by hand; the page places it otherwise */
  const spotFor = (size: { width: number; height: number }, wanted: Point): Point | null =>
    (byHand ? freeSpotNear(size, Object.values(placement.cards), wanted) : null);

  const make = async (kind: 'card' | 'ask' | 'screenplay') => {
    if (!canEdit || !projectId || !user || making) return;
    setMaking(true);
    setNotice(null);
    try {
      const size = { width: kind === 'screenplay' ? SCREENPLAY_CARD_WIDTH : COLUMN, height: GUESSED_HEIGHT[kind] };
      const middle = viewMiddle();
      const at = spotFor(size, { x: middle.x - size.width / 2, y: middle.y - size.height / 2 });
      const idea = await createIdea(projectId, user, kind === 'ask'
        ? { kind: 'ask', ask: { topic: '', buildOnKept: true }, at }
        : kind === 'screenplay' ? { kind: 'screenplay', screenplay: { title: '', author: user.name, paper: 'A4', draft: 1, versions: [] }, at } : { at });
      showAhead(idea);
      setMadeId(idea.id);
      requestAnimationFrame(() => panTo(idea.id, kind === 'screenplay'));
    } catch (e) {
      setNotice(errorText(e, 'The card could not be made.'));
    } finally {
      setMaking(false);
    }
  };

  /**
   * A change to a card: shown at once, then made on the cloud's copy. It is made on the card as
   * it is now, not as the handler last saw it: a save of the words followed by a save of a
   * version must not show the words as they were before.
   */
  const change = async (idea: Idea, apply: (current: Idea, at: number) => Idea, send: () => Promise<unknown>) => {
    const current = freshest(idea);
    const next = apply(current, Date.now());
    if (next === current) return;
    showAhead(next);
    try { await send(); } catch (e) { dropAhead(idea.id); throw e; }
  };

  /** The model's answer; a thinking model is told how hard to think, with room for it; a Venice answer is paid from the balance, which the top bar shows anew */
  const askModel = async (request: TextRequest): Promise<TextAnswer> => {
    const answer = await generateText(textKeys, withThinking(request, modelOption, thinking), modelId);
    if (modelId?.startsWith('venice:')) void refreshVeniceBalance(textKeys.venice, true);
    return answer;
  };
  /** What each of so many cards cost: the answer's price and tokens, shared out */
  const madeByOf = (answer: TextAnswer, cards: number): MadeBy => {
    const usd = costOfUsage(modelOption, answer);
    const share = (tokens: number | null) => (tokens === null ? null : Math.round(tokens / Math.max(1, cards)));
    return { model: modelName ?? 'a model', inputTokens: share(answer.inputTokens), outputTokens: share(answer.outputTokens), usd: usd === null ? null : usd / Math.max(1, cards) };
  };

  /** A Think up card asked: the answer lands beside it as one card, and the asking is remembered on it with its cost */
  const askFromCard = async (given: Idea) => {
    // As the card is now: the words just written are flushed to it a moment before the asking
    const idea = freshest(given);
    if (!projectId || !user || !idea.ask) return;
    setWorking({ id: idea.id, tool: 'ask' });
    try {
      const kept = idea.ask.buildOnKept ? keptTexts : [];
      const screenplayWanted = idea.ask.format === 'screenplay';
      // Beside its asking, on a board laid out by hand (the page puts it in the row otherwise)
      const askRect = placement.cards[idea.id];
      const at = idea.at && askRect
        ? spotFor({ width: screenplayWanted ? SCREENPLAY_CARD_WIDTH : cardWidth(2), height: screenplayWanted ? GUESSED_HEIGHT.screenplay : UNMEASURED }, { x: askRect.x + askRect.width + GAP, y: askRect.y })
        : null;
      let made: Idea[];
      let answer: TextAnswer;
      if (screenplayWanted) {
        // A screenplay: written whole from the card's words, as a screenplay card with its first version
        const pages = idea.ask.pages ?? 2;
        const paper = idea.ask.paper ?? 'A4';
        answer = await askModel(screenplayRequest('write', { brief: idea.ask.topic, pages }));
        const text = fountainFromAnswer(answer.text);
        if (!text) throw new Error('The model wrote nothing. Try again.');
        const parsed = parseFountain(text);
        const laid = paginate(parsed, paper).length;
        const screenplay: ScreenplayMeta = {
          title: parsed.titlePage.title || '', author: parsed.titlePage.author || user.name, paper, draft: 1,
          versions: [{ at: Date.now(), by: 'ai', byName: modelName ?? 'a model', note: 'Draft 1 · written from the brief', text, pages: laid }],
        };
        made = await createIdeas(projectId, user, [{ name: '', text, kind: 'screenplay', screenplay, at }], 'ai', { fromAskId: idea.id, madeBy: madeByOf(answer, 1), prompt: idea.ask.topic });
      } else if (idea.ask.format === 'prompt') {
        // A prompt for a node, by the generation prompt writer's method: the skill goes along, with the generator's facts and what the workspace holds
        const target = idea.ask.target ?? 'video';
        const generator = generatorOf(target, idea.ask.generator);
        const duration = target === 'video' ? lengthOf(generator, idea.ask.length) : undefined;
        const aspectRatio = frameOf(generator, idea.ask.frame);
        const skill = await loadPromptSkill(target);
        answer = await askModel(promptRequest({ brief: idea.ask.topic, target, generator, duration, aspectRatio, workspace: workspaceNotes(nodes), kept, skill }));
        const card = promptCardFromAnswer(answer.text, target === 'video' ? 'Video prompt' : 'Image prompt');
        if (!card.text) throw new Error('The model wrote nothing. Try again.');
        const promptFor: PromptFor = { node: target, model: generator?.id ?? '', ...(duration ? { duration } : {}), ...(aspectRatio ? { aspectRatio } : {}) };
        made = await createIdeas(projectId, user, [{ ...card, at }], 'ai', { fromAskId: idea.id, madeBy: madeByOf(answer, 1), prompt: idea.ask.topic, promptFor });
      } else {
        answer = await askModel(thinkRequest(idea.ask.topic, kept));
        const card = namedAnswer(answer.text);
        if (!card.text && !card.name) throw new Error('The model answered nothing. Try again.');
        made = await createIdeas(projectId, user, [{ ...card, at }], 'ai', { fromAskId: idea.id, madeBy: madeByOf(answer, 1), prompt: idea.ask.topic });
      }
      for (const written of made) showAhead(written);
      const run: AskRun = { at: Date.now(), model: modelName ?? 'a model', inputTokens: answer.inputTokens, outputTokens: answer.outputTokens, usd: costOfUsage(modelOption, answer), cards: made.length };
      showAhead(addRun(idea, run));
      recordAskRun(projectId, idea.id, run).catch((e) => console.warn('[ideas] the asking was not remembered on the card:', e));
    } finally {
      setWorking(null);
    }
  };

  /**
   * A screenplay card asked: the model writes it whole from the brief, rewrites one scene or
   * continues it. What results is the card's text, kept as a version with a note of what was done.
   */
  const writeScreenplay = async (idea: Idea, ask: ScreenplayAsk) => {
    if (!projectId || !user || idea.kind !== 'screenplay') return;
    const meta: ScreenplayMeta = idea.screenplay ?? { title: '', author: '', paper: 'A4', draft: 1, versions: [] };
    setWorking({ id: idea.id, tool: 'ask' });
    try {
      const sceneText = ask.scene ? idea.text.split('\n').slice(ask.scene.from, ask.scene.to).join('\n') : undefined;
      const answer = await askModel(screenplayRequest(ask.mode, { brief: ask.brief, pages: ask.pages, title: meta.title, author: meta.author, text: idea.text, sceneHeading: ask.scene?.heading, sceneText }));
      const written = fountainFromAnswer(answer.text);
      if (!written) throw new Error('The model wrote nothing. Try again.');
      const text = ask.mode === 'rewrite' && ask.scene ? replaceScene(idea.text, ask.scene, written)
        : ask.mode === 'continue' ? appendText(idea.text, written)
        : withTitlePage(written, { title: meta.title || undefined, author: meta.author || undefined });
      const parsed = parseFountain(text);
      const draft = ask.mode === 'write' && idea.text.trim() ? meta.draft + 1 : meta.draft;
      const note = ask.mode === 'rewrite' ? `rewrote scene ${(ask.scene?.index ?? 0) + 1}` : ask.mode === 'continue' ? `continued by ${ask.pages} page${ask.pages === 1 ? '' : 's'}` : `Draft ${draft} · written from the brief`;
      const next: ScreenplayMeta = {
        ...meta, draft,
        title: meta.title || parsed.titlePage.title || '', author: meta.author || parsed.titlePage.author || '',
        versions: addVersion(meta.versions, { at: Date.now(), by: 'ai', byName: modelName ?? 'a model', note, text, pages: paginate(parsed, meta.paper).length }),
      };
      const madeBy = madeByOf(answer, 1);
      await change(idea, (current, at) => ({ ...setScreenplay(setText(current, text, at), next, at), madeBy, prompt: ask.brief || current.prompt }),
        () => changeIdea(projectId, idea.id, (current, at) => ({ ...setScreenplay(setText(current, text, at), next, at), madeBy, prompt: ask.brief || current.prompt })).then(() => undefined));
    } finally {
      setWorking(null);
    }
  };

  /** A tool on a card: the answer is one new card; the card itself stays as it was */
  const useTool = async (idea: Idea, tool: CardTool) => {
    if (!projectId || !user) return;
    setWorking({ id: idea.id, tool });
    try {
      const answer = await askModel(cardToolRequest(tool, idea.text));
      const card = namedAnswer(answer.text);
      if (!card.text && !card.name) throw new Error('The model answered nothing. Try again.');
      const rect = placement.cards[idea.id];
      const at = idea.at && rect ? spotFor({ width: cardWidth(2), height: UNMEASURED }, { x: rect.x + rect.width + GAP, y: rect.y }) : null;
      const made = await createIdeas(projectId, user, [{ ...card, at }], 'ai', { madeBy: madeByOf(answer, 1), prompt: `${CARD_TOOLS.find(t => t.id === tool)?.label ?? tool} of "${titleOf(idea)}"` });
      for (const written of made) showAhead(written);
    } finally {
      setWorking(null);
    }
  };

  /**
   * A prompt card that was run and came out wrong: the model, with the prompt writer's skill,
   * finds the cause and rewrites the whole file one version on, as a new card beside this one.
   */
  const fixPrompt = async (idea: Idea, wentWrong: string) => {
    if (!projectId || !user) return;
    setWorking({ id: idea.id, tool: 'fix' });
    try {
      const target = idea.promptFor?.node ?? 'video';
      const generator = idea.promptFor?.model ? generatorOf(target, idea.promptFor.model) : null;
      const skill = await loadPromptSkill(target);
      const answer = await askModel(fixPromptRequest({ file: idea.text, wentWrong, target, generator, duration: idea.promptFor?.duration, aspectRatio: idea.promptFor?.aspectRatio, skill }));
      const card = promptCardFromAnswer(answer.text, titleOf(idea));
      if (!card.text) throw new Error('The model wrote nothing. Try again.');
      const rect = placement.cards[idea.id];
      const at = idea.at && rect ? spotFor({ width: cardWidth(2), height: UNMEASURED }, { x: rect.x + rect.width + GAP, y: rect.y }) : null;
      const made = await createIdeas(projectId, user, [{ ...card, at }], 'ai', { fromAskId: idea.fromAskId, madeBy: madeByOf(answer, 1), prompt: `Fix of "${titleOf(idea)}": ${wentWrong}`, promptFor: idea.promptFor });
      for (const written of made) showAhead(written);
    } finally {
      setWorking(null);
    }
  };

  const nameIt = async (idea: Idea) => {
    if (!projectId || !user) return;
    setWorking({ id: idea.id, tool: 'name' });
    try {
      const answer = await askModel(nameRequest(idea.text));
      const name = namesFromAnswer(answer.text, 1)[0];
      if (!name) throw new Error('The model gave no name. Try again.');
      await change(idea, (current, at) => setName(current, name, at), () => saveIdeaWords(projectId, idea.id, { name }));
    } finally {
      setWorking(null);
    }
  };

  const rail = `absolute top-1/2 -translate-y-1/2 z-40 pointer-events-auto group/rail transition-[left] duration-200 ${panelOpen ? 'left-[328px]' : 'left-4'}`;

  return (
    <div className="absolute inset-0 z-30 bg-cream text-ink overflow-hidden select-none" data-ideas-page>
      {/* The board: the dot grid of the canvas, and the cards on their layer */}
      <div
        ref={viewportRef}
        className="absolute inset-0 overflow-hidden cursor-grab data-[panning]:cursor-grabbing"
        style={{ backgroundImage: 'radial-gradient(rgb(var(--c-ink) / 0.10) 1px, transparent 1px)', backgroundSize: '24px 24px' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        // The board moves by its transform alone: a focus the browser scrolls to must not scroll this frame
        onScroll={(e) => { e.currentTarget.scrollTop = 0; e.currentTarget.scrollLeft = 0; }}
        data-ideas-board
      >
        <div ref={layerRef} className="absolute left-0 top-0 origin-top-left will-change-transform select-text" data-ideas-layer>
          {shown.length > 0 && user && projectId && (
            <div className="relative" style={{ width: layout.width + 2 * PAD + (moving ? Math.max(0, moving.dx) : 0), height: layout.height + 2 * PAD + (moving ? Math.max(0, moving.dy) : 0) }} data-ideas-grid>
              {shown.map(idea => (
              <div
                key={idea.id}
                ref={slotRef(idea.id)}
                className={`absolute ${moving?.id === idea.id ? 'z-20 cursor-grabbing drop-shadow-lift select-none' : ''}`}
                style={{ left: PAD + placeOf(idea.id).x - layout.left, top: PAD + placeOf(idea.id).y - layout.top, width: layout.places[idea.id].width, visibility: layout.places[idea.id].measured ? 'visible' : 'hidden' }}
                onPointerDown={onCardPointerDown(idea)}
                onMouseDown={onCardMouseDown(idea)}
                onPointerMove={onCardPointerMove}
                onPointerUp={onCardPointerUp}
                onPointerCancel={onCardPointerUp}
                onClickCapture={onCardClickCapture}
                data-card-slot={idea.id}
                data-card-width={layout.places[idea.id].width}
                data-card-placed={idea.at ? '1' : undefined}
              >
              {idea.kind === 'screenplay' ? (
                <ScreenplayCard
                  idea={idea}
                  userId={user.id}
                  userName={user.name}
                  people={people}
                  canEditWorkspace={canEdit}
                  options={options}
                  modelId={modelId}
                  onModelChange={chooseModel}
                  thinking={thinking}
                  onThinkingChange={chooseThinkingLevel}
                  busy={working?.id === idea.id}
                  now={now}
                  autoFocus={idea.id === madeId}
                  onAutoFocused={clearMade}
                  highlighted={idea.id === litId}
                  focused={openId === idea.id}
                  onOpen={() => setOpenId(idea.id)}
                  onClose={() => setOpenId(null)}
                  onAskModel={askModel}
                  onSaveText={(text) => change(idea, (current, at) => setText(current, text, at), () => saveIdeaWords(projectId, idea.id, { kind: 'screenplay', text }))}
                  onSaveMeta={(meta) => change(idea, (current, at) => setScreenplay(current, meta, at), () => saveIdeaWords(projectId, idea.id, { kind: 'screenplay', screenplay: meta }))}
                  onWrite={(ask) => writeScreenplay(idea, ask)}
                  onKeep={(locked) => change(idea, (current, at) => setLocked(current, locked, user, at), () => keepIdea(projectId, idea.id, locked, user))}
                  onDelete={async () => { await deleteIdea(projectId, idea.id); dropAhead(idea.id); }}
                  onPagesShown={() => requestAnimationFrame(() => panTo(idea.id, true))}
                />
              ) : idea.kind === 'ask' ? (
                <AskCard
                  idea={idea}
                  canEdit={canEdit}
                  canDelete={canEdit && (idea.createdBy === user.id || people.some(person => person.id === user.id && person.permission === 'owner'))}
                  options={options}
                  modelId={modelId}
                  onModelChange={chooseModel}
                  thinking={thinking}
                  onThinkingChange={chooseThinkingLevel}
                  keptTexts={keptTexts}
                  busy={working?.id === idea.id}
                  now={now}
                  autoFocus={idea.id === madeId}
                  onAutoFocused={clearMade}
                  highlighted={idea.id === litId}
                  onSaveAsk={(ask: Partial<IdeaAsk>) => change(idea, (current, at) => setAsk(current, ask, at), () => saveIdeaWords(projectId, idea.id, { ask }))}
                  onAsk={() => askFromCard(idea)}
                  onDelete={async () => { await deleteIdea(projectId, idea.id); dropAhead(idea.id); }}
                />
              ) : (
                <IdeaCard
                  idea={idea}
                  userId={user.id}
                  people={people}
                  canEditWorkspace={canEdit}
                  toolsAvailable={toolsAvailable}
                  toolsModel={modelName}
                  working={working?.id === idea.id && working.tool !== 'ask' ? working.tool : null}
                  now={now}
                  autoEdit={idea.id === madeId}
                  onAutoEdited={clearMade}
                  highlighted={idea.id === litId}
                  onSaveText={(text) => change(idea, (current, at) => setText(current, text, at), () => saveIdeaWords(projectId, idea.id, { text }))}
                  onSaveName={(name) => change(idea, (current, at) => setName(current, name, at), () => saveIdeaWords(projectId, idea.id, { name }))}
                  onNameIt={() => nameIt(idea)}
                  onReact={(emoji) => change(idea, (current, at) => toggleReaction(current, emoji, user.id, at), () => reactToIdea(projectId, idea.id, emoji, user))}
                  onKeep={(locked) => change(idea, (current, at) => setLocked(current, locked, user, at), () => keepIdea(projectId, idea.id, locked, user))}
                  onGive={(personId) => {
                    const person = personId ? people.find(p => p.id === personId) ?? null : null;
                    return change(idea, (current, at) => giveIdea(current, person, at), () => giveIdeaTo(people, projectId, idea.id, personId, user));
                  }}
                  onTick={(done) => change(idea, (current, at) => setDone(current, done, user, at), () => finishIdea(people, projectId, idea.id, done, user))}
                  onTool={(tool) => useTool(idea, tool)}
                  onSendToCanvas={onSendToCanvas}
                  onFix={canEdit ? (wentWrong) => fixPrompt(idea, wentWrong) : undefined}
                  onDelete={async () => { await deleteIdea(projectId, idea.id); dropAhead(idea.id); }}
                  onProblem={setNotice}
                />
              )}
              </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* What there is to say when the board shows nothing */}
      {(!user || !projectId || loadError || !ideas || ideas.length === 0) && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none pt-[72px]">
          <div className="pointer-events-auto max-w-[520px]" data-board-bar>
            {!user ? (
              <Empty icon={<CloudOff aria-hidden="true" />} title="Sign in to write ideas" text="Ideas are written with the people of a workspace, so they need you signed in." />
            ) : !projectId ? (
              <Empty icon={<CloudOff aria-hidden="true" />} title="This workspace is on this device only" text="Ideas and tasks are shared with the people of a workspace, so they live in the cloud. Save this workspace to the cloud, then come back here." />
            ) : loadError ? (
              <div className="ns-note" data-tone="danger" role="alert">
                <span className="flex-1">{loadError}</span>
                <button type="button" onClick={() => setRound(n => n + 1)} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat">Try again</button>
              </div>
            ) : !ideas ? (
              <div className="flex items-center gap-2 text-sm text-ink-faint" data-ideas-loading><Loader2 size={14} className="animate-spin" aria-hidden="true" /> Loading the board</div>
            ) : (
              <Empty
                icon={<Lightbulb aria-hidden="true" />}
                title="A board for thinking"
                text={canEdit
                  ? 'Write a card for every idea: a logline, a scene, a line of dialogue, a thought. Keep the ones you like, react to each other\'s, and give a card to someone as a task. The rail on the left adds a card, a Think up card that has a model write for you, or a screenplay to write on its pages.'
                  : 'Nothing on the board yet.'}
                action={canEdit ? (
                  <span className="flex flex-wrap justify-center gap-2">
                    <button type="button" onClick={() => void make('card')} disabled={making} className="ns-btn ns-btn-primary"><Plus size={16} aria-hidden="true" /> New card</button>
                    <button type="button" onClick={() => void make('ask')} disabled={making} className="ns-btn ns-btn-soft"><Sparkles size={16} aria-hidden="true" /> Think up ideas</button>
                    <button type="button" onClick={() => void make('screenplay')} disabled={making} className="ns-btn ns-btn-soft"><FileText size={16} aria-hidden="true" /> New screenplay</button>
                  </span>
                ) : undefined}
              />
            )}
          </div>
        </div>
      )}

      {notice && (
        <div className="ns-note absolute top-[72px] left-1/2 -translate-x-1/2 z-50 max-w-[560px]" data-tone="warn" role="alert" onPointerDown={(e) => e.stopPropagation()}>
          <span className="flex-1">{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="ns-icon-btn ns-icon-btn-xs -my-1 -mr-1 text-current"><X /></button>
        </div>
      )}

      {/* The rail: what the board adds, as the node rail adds nodes */}
      {user && projectId && (
        <div className={rail} data-board-rail>
          <div className="ns-glass ns-glass-strong flex flex-col gap-1 rounded-3xl p-1 w-[42px] group-hover/rail:w-48 transition-[width] duration-200 ease-[cubic-bezier(0.34,1.45,0.5,1)] overflow-hidden">
            {canEdit && (
              <>
                <RailButton tone="lavender" label="New card" hint="A card to write on" onClick={() => void make('card')} disabled={making} data="new-idea"><Plus size={16} /></RailButton>
                <RailButton tone="flame" label="Think up" hint="A card that has a model write a card, or a screenplay" onClick={() => void make('ask')} disabled={making} data="generate-ideas"><Sparkles size={16} /></RailButton>
                <RailButton tone="lemon" label="New screenplay" hint="A screenplay: a sheet of A4 to write on, with every line in its place, versions, and PDF or Fountain to take away" onClick={() => void make('screenplay')} disabled={making} data="new-screenplay"><FileText size={16} /></RailButton>
              </>
            )}
            <RailButton tone="mint" label="People" hint="Who is in this workspace: give cards to them, or bring someone in" onClick={onShare} data="people"><Users size={16} /></RailButton>
          </div>
        </div>
      )}

      {/* The zoom rail, as on the canvas */}
      <div className="ns-glass ns-glass-strong ns-bar absolute bottom-4 z-50 flex flex-col items-center gap-0.5 p-1" style={{ right: 'calc(16px + var(--canvas-right-inset, 0px))' }} onPointerDown={(e) => e.stopPropagation()} data-board-zoom>
        <button type="button" onClick={() => zoomAt(1.2)} className="ns-icon-btn" title="Zoom in" aria-label="Zoom in"><Plus /></button>
        <button type="button" onClick={() => setView({ scale: 1 })} className="ns-t-meta w-8 py-1 rounded-md text-center tabular-nums hover:text-ink" title="Reset zoom" data-board-zoom-level>{zoomPercent}%</button>
        <button type="button" onClick={() => zoomAt(1 / 1.2)} className="ns-icon-btn" title="Zoom out" aria-label="Zoom out"><Minus /></button>
        <button type="button" onClick={fit} className="ns-icon-btn" title="Fit the board in view" aria-label="Fit the board in view"><Maximize2 /></button>
      </div>
    </div>
  );
};

const RailButton: React.FC<{ tone: string; label: string; hint: string; onClick: () => void; disabled?: boolean; data: string; children: React.ReactNode }> = ({ tone, label, hint, onClick, disabled, data, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex items-center gap-3 w-full h-8 pr-3 rounded-full hover:bg-cream-200 text-ink transition-colors shrink-0 disabled:opacity-60"
    title={hint}
    aria-label={label}
    data-tone={tone}
    {...{ [`data-${data}`]: '' }}
  >
    {/* The same round chip, in the same colour, as the node rail's */}
    <span className="ns-rail-chip">{children}</span>
    <span className="text-xs font-semibold whitespace-nowrap opacity-0 group-hover/rail:opacity-100 transition-opacity duration-150">{label}</span>
  </button>
);

const Empty: React.FC<{ icon?: React.ReactNode; title: string; text: string; action?: React.ReactNode }> = ({ icon, title, text, action }) => (
  <div className="ns-empty" data-tasks-empty>
    {icon && <span className="ns-mark ns-mark-lg ns-mark-lavender">{icon}</span>}
    <p className="ns-empty-title">{title}</p>
    <p className="ns-empty-text">{text}</p>
    {action}
  </div>
);

export default IdeasPage;
